import type { UserProfile, UserRole } from '@/types';

/**
 * Check if a user has at least the specified role level.
 * Role hierarchy: admin > leader > member
 */
export function hasRole(profile: UserProfile | null, role: UserRole): boolean {
  if (!profile) return false;
  const userRole = profile.role || 'member';

  // Member is the base role - everyone has it
  if (role === 'member') return true;

  // Leader role includes admin (admin is a "super leader")
  if (role === 'leader') return userRole === 'leader' || userRole === 'admin';

  // Admin is the highest role
  if (role === 'admin') return userRole === 'admin';

  return false;
}

/**
 * Check if user is a leader or admin (can use moderation features)
 */
export function isLeaderOrAdmin(profile: UserProfile | null): boolean {
  return hasRole(profile, 'leader');
}

/**
 * Check if user is an admin (can access admin panel)
 */
export function isAdmin(profile: UserProfile | null): boolean {
  return hasRole(profile, 'admin');
}

/**
 * Get the display role for UI badges
 * Returns null for regular members (no badge shown)
 */
export function getDisplayRole(profile: UserProfile | null): 'moderator' | null {
  if (!profile) return null;
  const userRole = profile.role || 'member';
  // Both leaders and admins show the same "moderator" badge
  if (userRole === 'leader' || userRole === 'admin') {
    return 'moderator';
  }
  return null;
}

/**
 * Check if the current user can initiate a DM to the target user based on roles.
 * DM permission hierarchy:
 * - Admins can DM anyone (admins, leaders, members)
 * - Leaders can DM leaders and members (not admins)
 * - Members cannot initiate DMs
 */
export function canInitiateDMTo(
  currentUserRole: UserRole | undefined,
  targetUserRole: UserRole | undefined
): boolean {
  const currentRole = currentUserRole || 'member';
  const targetRole = targetUserRole || 'member';

  if (currentRole === 'admin') {
    // Admins can message anyone
    return true;
  } else if (currentRole === 'leader') {
    // Leaders can message other leaders and members, but not admins
    return targetRole !== 'admin';
  }
  // Members cannot initiate DMs
  return false;
}

/**
 * TEMPORARY RESTRICTION: Only leaders and admins can post to community chat.
 * Regular members can read but not post.
 * To revert: remove this function and its usage in ChatSection.tsx
 */
export function canPostToCommunity(profile: UserProfile | null): boolean {
  return isLeaderOrAdmin(profile);
}

/**
 * Check if user can post images in community chat.
 * - Leaders and admins can always post images
 * - Members need explicit canPostCommunityImages capability
 */
export function canPostCommunityImages(profile: UserProfile | null): boolean {
  if (!profile) return false;
  // Leaders and admins always can
  if (isLeaderOrAdmin(profile)) return true;
  // Members need explicit capability
  return profile.canPostCommunityImages === true;
}

/**
 * Check if user can enroll in MFA (Multi-Factor Authentication).
 *
 * MFA is available to:
 * - All users with email-based authentication (password or email link)
 * - Leaders and admins (regardless of auth provider)
 *
 * OAuth users (Google, Microsoft, Apple) should use their provider's MFA instead,
 * unless they are leaders/admins who may want an additional layer.
 *
 * The core MFA functions in auth.ts are role-agnostic - this function
 * only controls UI visibility for the enrollment option.
 */
export function canEnrollMfa(profile: UserProfile | null): boolean {
  if (!profile) return false;

  // Leaders and admins can always enroll
  if (isLeaderOrAdmin(profile)) {
    return true;
  }

  // All users with email-based auth can enroll
  const isEmailAuth = profile.provider === 'password' || profile.provider === 'emailLink';
  if (isEmailAuth) {
    return true;
  }

  return false;
}

/**
 * Check if user is required to enroll in MFA.
 * This is set by admins via the force MFA enrollment feature.
 */
export function isMfaRequired(profile: UserProfile | null): boolean {
  if (!profile) return false;
  return profile.mfaRequired === true && !profile.mfaEnabled;
}

/**
 * Check if user's MFA grace period has expired.
 * Returns true if user is required to set up MFA and grace period has passed.
 */
export function isMfaGraceExpired(profile: UserProfile | null): boolean {
  if (!profile) return false;
  if (!profile.mfaRequired || profile.mfaEnabled) return false;
  if (profile.mfaRequiredEnforcement !== 'grace_period') return false;
  if (!profile.mfaGraceDeadline) return false;

  const now = Date.now();
  // Support both Firestore Timestamp and JS Date
  const deadline =
    typeof profile.mfaGraceDeadline.toMillis === 'function'
      ? profile.mfaGraceDeadline.toMillis()
      : new Date(profile.mfaGraceDeadline as unknown as string | number).getTime();
  return now > deadline;
}
